import type { Tenant, Post, Author, Media, Category } from '../../payload-types'
import { siteUrl, postUrl, categoryUrl } from '../site'
import { clampDescription } from './meta'

const rel = (obj: unknown) => (obj && typeof obj === 'object' ? (obj as Record<string, unknown>) : null)

/** URL del media (Bunny assoluto o url locale). Esportato per og:image. */
export const mediaUrl = (m: unknown): string | undefined => {
  const media = rel(m) as Media | null
  return (media?.bunnyUrl as string) || (media?.url as string) || undefined
}

/** Normalizza una data a ISO 8601; undefined se assente/invalida. */
const iso = (v: unknown): string | undefined => {
  if (!v) return undefined
  const d = new Date(v as string | number | Date)
  return isNaN(d.getTime()) ? undefined : d.toISOString()
}

/**
 * Serializza JSON-LD per l'iniezione SSR in modo SICURO: neutralizza `</script>`,
 * `&` e i separatori di riga U+2028/U+2029 (XSS stored/SSR). Da usare SEMPRE al posto
 * di JSON.stringify dentro dangerouslySetInnerHTML.
 */
export const serializeJsonLd = (obj: unknown): string =>
  JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')

/**
 * Grafo JSON-LD unico per la pagina articolo, con relazioni @id, replica della
 * struttura @graph di Yoast: Article, WebPage, ImageObject, BreadcrumbList,
 * WebSite (+SearchAction), Organization, Person.
 *
 * INVARIANTE, e non è una scelta di implementazione ma un vincolo di rischio:
 *
 *   IL GRAFO NON LEGGE MAI IL CORPO DELL'ARTICOLO, NÉ ALCUN CAMPO CHE UN LLM POSSA SCRIVERE
 *   IN FORMA LIBERA, E NON EMETTE NESSUN NODO DI LUOGO.
 *
 * Vietati a qualunque condizione: Place, TouristAttraction, LocalBusiness, offers, price,
 * isAccessibleForFree, openingHoursSpecification, geo, latitude/longitude, address.
 *
 * Perché. Durante l'audit un agente dava per accertato «ingresso 1,00 euro» e voleva emetterlo
 * come `offers.price`: il grep sui nove documenti reali dava ZERO occorrenze di «€», «euro»,
 * «ingresso», «biglietto», e l'unica attestazione erano recensioni utente del 2017-2019 con una
 * variante concorrente («se non ricordiamo male sono 2 euro»). Emetterlo avrebbe reso questo sito
 * IL PRIMO POSTO SUL WEB in cui quel dato esiste in forma leggibile dalle macchine: non ripetere
 * un errore, generarlo. In un dato strutturato l'errore è spam macchina-leggibile, che è peggio
 * del thin content, e circola senza il contesto che lo smentisce.
 *
 * Il grafo povero è l'unica ragione per cui il claim falso di accessibilità non è diventato dato
 * strutturato: va protetto per costruzione, non per fortuna. Lo asserisce jsonld.test.ts.
 * Si rinuncia deliberatamente ai rich result per le attrazioni.
 */
export function buildArticleGraph(tenant: Tenant, post: Post) {
  const base = siteUrl(tenant)
  const pageUrl = postUrl(tenant, post.slug)
  const orgId = `${base}/#organization`
  const websiteId = `${base}/#website`
  const webpageId = `${pageUrl}#webpage`
  const articleId = `${pageUrl}#article`
  const imageId = `${pageUrl}#primaryimage`

  const author = rel(post.author) as Author | null
  const authorId = author ? `${base}/#/schema/person/${author.id}` : undefined
  const img = mediaUrl(post.featuredImage)
  const orgType = tenant.brand?.organizationType || 'Organization'
  // freshness: ricade su publishedAt, MAI su updatedAt (che cambia a ogni save tecnico)
  const published = iso(post.publishedAt)
  const modifiedRaw = iso(post.contentUpdatedAt) ?? published
  // dateModified non può precedere datePublished (sarebbe invalido per schema.org)
  const modified = modifiedRaw && published && modifiedRaw < published ? published : modifiedRaw
  // categoria per breadcrumb e articleSection: primaryCategory, fallback alla prima
  const primaryCat = rel(post.seo?.primaryCategory) as Category | null
  const firstCat =
    Array.isArray(post.categories) && post.categories[0] && typeof post.categories[0] === 'object'
      ? (post.categories[0] as Category)
      : null
  const sectionCat = primaryCat ?? firstCat

  const graph: Record<string, unknown>[] = []

  graph.push({
    '@type': 'Article',
    '@id': articleId,
    isPartOf: { '@id': webpageId },
    headline: (post.title || '').slice(0, 110),
    datePublished: published,
    dateModified: modified,
    ...(authorId ? { author: { '@id': authorId } } : {}),
    ...(sectionCat ? { articleSection: sectionCat.name } : {}),
    publisher: { '@id': orgId },
    mainEntityOfPage: { '@id': webpageId },
    ...(img ? { image: { '@id': imageId } } : {}),
    inLanguage: post.locale,
  })

  graph.push({
    '@type': 'WebPage',
    '@id': webpageId,
    url: pageUrl,
    name: post.seo?.title || post.title,
    isPartOf: { '@id': websiteId },
    ...(img ? { primaryImageOfPage: { '@id': imageId }, image: { '@id': imageId } } : {}),
    datePublished: published,
    dateModified: modified,
    description: clampDescription(post.seo?.metaDescription || post.excerpt),
    breadcrumb: { '@id': `${pageUrl}#breadcrumb` },
    inLanguage: post.locale,
  })

  if (img) {
    graph.push({
      '@type': 'ImageObject',
      '@id': imageId,
      url: img,
      contentUrl: img,
      caption: (rel(post.featuredImage) as Media | null)?.alt || undefined,
    })
  }

  const crumbs: Record<string, unknown>[] = [{ '@type': 'ListItem', position: 1, name: 'Home', item: base }]
  if (primaryCat) {
    crumbs.push({ '@type': 'ListItem', position: 2, name: primaryCat.name, item: categoryUrl(tenant, primaryCat.slug) })
  }
  crumbs.push({ '@type': 'ListItem', position: crumbs.length + 1, name: post.title })
  graph.push({ '@type': 'BreadcrumbList', '@id': `${pageUrl}#breadcrumb`, itemListElement: crumbs })

  graph.push({
    '@type': 'WebSite',
    '@id': websiteId,
    url: base,
    name: tenant.brand?.siteName || tenant.name,
    publisher: { '@id': orgId },
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${base}/search?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
    inLanguage: tenant.locale,
  })

  const logo = mediaUrl(tenant.brand?.logo)
  graph.push({
    '@type': orgType,
    '@id': orgId,
    name: tenant.brand?.siteName || tenant.name,
    url: base,
    ...(logo ? { logo: { '@type': 'ImageObject', url: logo } } : {}),
    ...(Array.isArray(tenant.brand?.social) && tenant.brand.social.length
      ? { sameAs: tenant.brand.social.map((s) => s.url).filter(Boolean) }
      : {}),
  })

  // Il nodo Person si emette SOLO se dietro c'è una persona documentata.
  //
  // Asserire in modo leggibile dalle macchine che un testo ha un autore, quando la scheda di
  // quell'autore è vuota (bio NULL su tutti e cinque gli autori del sito, uno dei quali è un
  // nickname), significa attribuire a una persona nominata un testo che non ha letto: è l'unico
  // rischio di questo dossier che ricade su un individuo invece che su un dominio. La byline resta
  // in pagina; ciò che si toglie è la sua affermazione nel grafo finché non è vera.
  if (author && authorId && author.bio) {
    graph.push({
      '@type': 'Person',
      '@id': authorId,
      name: author.name,
      description: author.bio,
      ...(mediaUrl(author.avatar) ? { image: mediaUrl(author.avatar) } : {}),
      ...(Array.isArray(author.social) && author.social.length
        ? { sameAs: author.social.map((s) => s.url).filter(Boolean) }
        : {}),
    })
  }

  return { '@context': 'https://schema.org', '@graph': graph }
}

/** Grafo JSON-LD per le pagine archivio (categoria/tag/autore): CollectionPage + breadcrumb. */
export function buildCollectionGraph(tenant: Tenant, opts: { url: string; name: string; description?: string }) {
  const base = siteUrl(tenant)
  const orgId = `${base}/#organization`
  const websiteId = `${base}/#website`
  const description = clampDescription(opts.description)
  const logo = mediaUrl(tenant.brand?.logo)
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${opts.url}#webpage`,
        url: opts.url,
        name: opts.name,
        ...(description ? { description } : {}),
        isPartOf: { '@id': websiteId },
        breadcrumb: { '@id': `${opts.url}#breadcrumb` },
        inLanguage: tenant.locale,
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${opts.url}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: base },
          { '@type': 'ListItem', position: 2, name: opts.name },
        ],
      },
      {
        '@type': 'WebSite',
        '@id': websiteId,
        url: base,
        name: tenant.brand?.siteName || tenant.name,
        publisher: { '@id': orgId },
        inLanguage: tenant.locale,
      },
      {
        '@type': tenant.brand?.organizationType || 'Organization',
        '@id': orgId,
        name: tenant.brand?.siteName || tenant.name,
        url: base,
        ...(logo ? { logo: { '@type': 'ImageObject', url: logo } } : {}),
        ...(Array.isArray(tenant.brand?.social) && tenant.brand.social.length
          ? { sameAs: tenant.brand.social.map((s) => s.url).filter(Boolean) }
          : {}),
      },
    ],
  }
}

/** Grafo JSON-LD per la homepage: WebSite (+SearchAction) + Organization. */
export function buildHomeGraph(tenant: Tenant) {
  const base = siteUrl(tenant)
  const orgId = `${base}/#organization`
  const websiteId = `${base}/#website`
  const logo = mediaUrl(tenant.brand?.logo)
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': websiteId,
        url: base,
        name: tenant.brand?.siteName || tenant.name,
        ...(tenant.brand?.tagline ? { description: tenant.brand.tagline } : {}),
        publisher: { '@id': orgId },
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${base}/search?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
        inLanguage: tenant.locale,
      },
      {
        '@type': tenant.brand?.organizationType || 'Organization',
        '@id': orgId,
        name: tenant.brand?.siteName || tenant.name,
        url: base,
        ...(logo ? { logo: { '@type': 'ImageObject', url: logo } } : {}),
        ...(Array.isArray(tenant.brand?.social) && tenant.brand.social.length
          ? { sameAs: tenant.brand.social.map((s) => s.url).filter(Boolean) }
          : {}),
      },
    ],
  }
}
